import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Command, CommandDialog, CommandEmpty, CommandGroup as CommandGroupComponent, CommandInput, CommandItem, CommandList, CommandShortcut } from '../ui/command'
import { 
  PlusIcon, 
  SearchIcon, 
  CalendarIcon, 
  MessageSquareIcon, 
  FileTextIcon, 
  LayoutGridIcon,
  Columns,
  ZapIcon,
  UserIcon,
  SettingsIcon,
  LogOutIcon,
  FolderIcon,
  TagIcon,
  ClockIcon,
  TrendingUpIcon,
  BarChart3Icon,
  UsersIcon,
  BellIcon,
  StarIcon,
  BookmarkIcon,
  ArchiveIcon,
  TrashIcon,
  FilterIcon,
  SortAscIcon,
  DownloadIcon,
  UploadIcon,
  ShareIcon,
  CopyIcon,
  RefreshCwIcon,
  MaximizeIcon,
  MinimizeIcon,
  LayoutIcon,
  GridIcon,
  ListIcon,
  MapIcon,
  PieChartIcon,
  BarChartIcon,
  LineChartIcon,
  CommandIcon
} from 'lucide-react'
import { trpc } from '../../lib/trpc'
import { useAuthStore } from '../../stores/auth-store'
import { Badge } from '../ui/badge'
import { shouldProcessHotkey } from '../../lib/keyboard-utils'

interface CommandPaletteProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  onNavigate?: (path: string) => void
  onCreatePanel?: (type: string) => void
  onAction?: (action: string) => void
}

// Command item interface with better typing
interface CommandItemMetadata {
  priority?: string
  status?: string
  priorityClass?: string
  location?: string
  locationType?: string
  assignee?: string
}

interface CommandItem {
  id: string
  title: string
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  shortcut?: string
  action: () => void
  group: CommandGroup
  keywords?: string[]
  priority?: number
  metadata?: CommandItemMetadata
}

// Command groups with explicit typing
enum CommandGroup {
  QUICK_ACTIONS = 'Quick Actions',
  AI_ASSISTANT = 'AI Assistant',
  NAVIGATION = 'Navigation',
  PANEL_MANAGEMENT = 'Panel Management',
  VIEW_OPTIONS = 'View Options',
  SETTINGS = 'Settings',
  WORKSPACES = 'Workspaces',
  TASKS = 'Tasks',
  NOTES = 'Notes',
  RECENT = 'Recent'
}

// Priority configuration for group ordering
const GROUP_PRIORITIES = {
  default: {
    [CommandGroup.QUICK_ACTIONS]: 1,
    [CommandGroup.TASKS]: 2,
    [CommandGroup.RECENT]: 3,
    [CommandGroup.AI_ASSISTANT]: 4,
    [CommandGroup.NOTES]: 5,
    [CommandGroup.NAVIGATION]: 6,
    [CommandGroup.WORKSPACES]: 7,
    [CommandGroup.PANEL_MANAGEMENT]: 8,
    [CommandGroup.VIEW_OPTIONS]: 9,
    [CommandGroup.SETTINGS]: 10
  },
  searching: {
    [CommandGroup.TASKS]: 1,
    [CommandGroup.NOTES]: 2,
    [CommandGroup.RECENT]: 3,
    [CommandGroup.QUICK_ACTIONS]: 4,
    [CommandGroup.AI_ASSISTANT]: 5,
    [CommandGroup.NAVIGATION]: 6,
    [CommandGroup.WORKSPACES]: 7,
    [CommandGroup.PANEL_MANAGEMENT]: 8,
    [CommandGroup.VIEW_OPTIONS]: 9,
    [CommandGroup.SETTINGS]: 10
  }
}

// Visual configuration constants
const PRIORITY_COLORS = {
  critical: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-green-600',
  normal: 'text-gray-600'
} as const

const STATUS_ICONS = {
  TODO: '○',
  IN_PROGRESS: '◐',
  REVIEW: '◑',
  DONE: '●'
} as const

export default function CommandPalette({
  isOpen,
  onOpenChange,
  workspaceId,
  onNavigate,
  onCreatePanel,
  onAction
}: CommandPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const { user } = useAuthStore()

  // Debounce search query for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 150) // 150ms debounce

    return () => clearTimeout(timer)
  }, [searchQuery])
  
  // Fetch recent items and workspaces
  const { data: recentItems = [] } = trpc.activity.getRecent.useQuery(
    { workspaceId, limit: 10 },
    { enabled: !!workspaceId }
  )
  
  const { data: workspaces = [] } = trpc.workspace.list.useQuery()
  
  // Use search.tasks endpoint - DON'T filter on frontend, let backend handle it
  const { data: searchTasks = [], isLoading } = trpc.search.tasks.useQuery(
    { 
      workspaceId, 
      query: debouncedQuery, 
      limit: 50 
    },
    { 
      enabled: !!workspaceId,
      keepPreviousData: false, // Don't keep previous data to see the actual results
    }
  )
  
  // Log the actual data we're getting
  useEffect(() => {
    if (debouncedQuery) {
      console.log('Debounced query:', debouncedQuery)
      console.log('Tasks returned:', searchTasks)
      console.log('Is loading:', isLoading)
    }
  }, [debouncedQuery, searchTasks, isLoading])
  
  // Fetch notes separately
  const { data: searchNotes = [] } = trpc.notes.search.useQuery(
    { workspaceId, query: debouncedQuery, limit: 10 },
    { enabled: !!workspaceId && debouncedQuery.length >= 1 }
  )
  
  // Helper function to create command items
  const createCommand = (
    id: string,
    title: string,
    config: Partial<CommandItem>
  ): CommandItem => ({
    id,
    title,
    subtitle: '',
    icon: PlusIcon,
    action: () => {},
    group: CommandGroup.QUICK_ACTIONS,
    keywords: [],
    priority: 0,
    ...config
  })

  // Quick action commands factory
  const createQuickActionCommands = useCallback((): CommandItem[] => [
    createCommand('quick-task', 'Create Task', {
      subtitle: 'Add new task to Kanban',
      icon: PlusIcon,
      shortcut: '⌘N',
      action: () => onCreatePanel?.('TASK'),
      group: CommandGroup.QUICK_ACTIONS,
      keywords: ['task', 'todo', 'create', 'add', 'kanban'],
      priority: 10
    }),
    createCommand('quick-note', 'Create Note', {
      subtitle: 'Start writing immediately',
      icon: FileTextIcon,
      shortcut: '⌘⇧N',
      action: () => onCreatePanel?.('NOTE'),
      keywords: ['note', 'write', 'document', 'create'],
      priority: 9
    }),
    createCommand('quick-event', 'Schedule Event', {
      subtitle: 'Add to calendar',
      icon: CalendarIcon,
      shortcut: '⌘⇧C',
      action: () => onCreatePanel?.('CALENDAR'),
      keywords: ['event', 'meeting', 'schedule', 'calendar'],
      priority: 8
    }),
    createCommand('quick-chat', 'Start Chat', {
      subtitle: 'Message team members',
      icon: MessageSquareIcon,
      shortcut: '⌘/',
      action: () => onCreatePanel?.('CHAT'),
      keywords: ['chat', 'message', 'communicate', 'talk'],
      priority: 7
    })
  ], [onCreatePanel])

  // AI Assistant commands factory
  const createAICommands = useCallback((): CommandItem[] => [
    createCommand('ai-analyze', 'AI Task Analysis', {
      subtitle: 'Get insights on your tasks',
      icon: TrendingUpIcon,
      action: () => onAction?.('ai-analyze'),
      group: CommandGroup.AI_ASSISTANT,
      keywords: ['ai', 'analyze', 'insights', 'smart', 'assistant'],
      priority: 6
    }),
    createCommand('ai-schedule', 'Smart Scheduling', {
      subtitle: 'AI-powered time blocking',
      icon: ZapIcon,
      action: () => onAction?.('ai-schedule'),
      group: CommandGroup.AI_ASSISTANT,
      keywords: ['ai', 'schedule', 'smart', 'time', 'block'],
      priority: 5
    }),
    createCommand('ai-suggest', 'Task Suggestions', {
      subtitle: 'Get AI recommendations',
      icon: StarIcon,
      action: () => onAction?.('ai-suggest'),
      group: CommandGroup.AI_ASSISTANT,
      keywords: ['ai', 'suggest', 'recommend', 'smart'],
      priority: 4
    })
  ], [onAction])

  // Navigation commands factory
  const createNavigationCommands = useCallback((): CommandItem[] => [
    createCommand('nav-dashboard', 'Go to Dashboard', {
      subtitle: 'Overview of all projects',
      icon: LayoutGridIcon,
      shortcut: '⌘1',
      action: () => onNavigate?.('/dashboard'),
      group: CommandGroup.NAVIGATION,
      keywords: ['dashboard', 'home', 'overview'],
      priority: 3
    }),
    createCommand('nav-projects', 'View Projects', {
      subtitle: 'All workspace projects',
      icon: FolderIcon,
      shortcut: '⌘2',
      action: () => onNavigate?.('/projects'),
      group: CommandGroup.NAVIGATION,
      keywords: ['projects', 'folders', 'workspace'],
      priority: 2
    }),
    createCommand('nav-team', 'Team Members', {
      subtitle: 'View and manage team',
      icon: UsersIcon,
      shortcut: '⌘3',
      action: () => onNavigate?.('/team'),
      group: CommandGroup.NAVIGATION,
      keywords: ['team', 'members', 'users', 'people'],
      priority: 1
    })
  ], [onNavigate])

  // Panel management commands factory
  const createPanelCommands = useCallback((): CommandItem[] => [
    createCommand('panel-layout-save', 'Save Layout', {
      subtitle: 'Save current panel arrangement',
      icon: BookmarkIcon,
      shortcut: '⌘S',
      action: () => onAction?.('save-layout'),
      group: CommandGroup.PANEL_MANAGEMENT,
      keywords: ['save', 'layout', 'panels', 'arrangement']
    }),
    createCommand('panel-layout-load', 'Load Layout', {
      subtitle: 'Switch to saved layout',
      icon: LayoutIcon,
      shortcut: '⌘L',
      action: () => onAction?.('load-layout'),
      group: CommandGroup.PANEL_MANAGEMENT,
      keywords: ['load', 'layout', 'switch', 'panels']
    }),
    createCommand('panel-focus', 'Focus Mode', {
      subtitle: 'Hide all but current panel',
      icon: MaximizeIcon,
      shortcut: '⌘⇧F',
      action: () => onAction?.('focus-mode'),
      group: CommandGroup.PANEL_MANAGEMENT,
      keywords: ['focus', 'hide', 'minimize', 'distraction-free']
    }),
    createCommand('panel-minimize-all', 'Minimize All', {
      subtitle: 'Collapse all panels',
      icon: MinimizeIcon,
      shortcut: '⌘⇧M',
      action: () => onAction?.('minimize-all'),
      group: CommandGroup.PANEL_MANAGEMENT,
      keywords: ['minimize', 'collapse', 'hide', 'panels']
    })
  ], [onAction])

  // View options commands factory
  const createViewCommands = useCallback((): CommandItem[] => [
    createCommand('view-grid', 'Grid View', {
      subtitle: 'Arrange panels in grid',
      icon: GridIcon,
      action: () => onAction?.('view-grid'),
      group: CommandGroup.VIEW_OPTIONS,
      keywords: ['grid', 'view', 'arrange', 'layout']
    }),
    createCommand('view-list', 'List View', {
      subtitle: 'Vertical panel layout',
      icon: ListIcon,
      action: () => onAction?.('view-list'),
      group: CommandGroup.VIEW_OPTIONS,
      keywords: ['list', 'view', 'vertical', 'layout']
    })
  ], [onAction])

  // Settings commands factory
  const createSettingsCommands = useCallback((): CommandItem[] => [
    createCommand('settings-workspace', 'Workspace Settings', {
      subtitle: 'Configure workspace',
      icon: SettingsIcon,
      action: () => onNavigate?.('/settings'),
      group: CommandGroup.SETTINGS,
      keywords: ['settings', 'configure', 'workspace', 'options']
    }),
    createCommand('settings-profile', 'Profile Settings', {
      subtitle: 'Update your profile',
      icon: UserIcon,
      action: () => onNavigate?.('/profile'),
      group: CommandGroup.SETTINGS,
      keywords: ['profile', 'account', 'user', 'settings']
    }),
    createCommand('logout', 'Sign Out', {
      subtitle: 'Log out of workspace',
      icon: LogOutIcon,
      action: () => onAction?.('logout'),
      group: CommandGroup.SETTINGS,
      keywords: ['logout', 'sign out', 'exit', 'leave']
    })
  ], [onNavigate, onAction])

  // Combine all static commands
  const staticCommands = useMemo(() => [
    ...createQuickActionCommands(),
    ...createAICommands(),
    ...createNavigationCommands(),
    ...createPanelCommands(),
    ...createViewCommands(),
    ...createSettingsCommands()
  ], [
    createQuickActionCommands,
    createAICommands,
    createNavigationCommands,
    createPanelCommands,
    createViewCommands,
    createSettingsCommands
  ])
  
  // Workspace commands factory
  const createWorkspaceCommands = useCallback((): CommandItem[] => 
    workspaces.map((workspace: any) => createCommand(
      `workspace-${workspace.id}`,
      `Switch to ${workspace.name}`,
      {
        subtitle: `${workspace.memberCount || 0} members`,
        icon: FolderIcon,
        action: () => onNavigate?.(`/workspace/${workspace.id}`),
        group: CommandGroup.WORKSPACES,
        keywords: ['workspace', 'switch', workspace.name.toLowerCase()],
        priority: workspace.id === workspaceId ? 10 : 1
      }
    )), [workspaces, workspaceId, onNavigate])

  // Task search commands factory
  const createTaskCommands = useCallback((): CommandItem[] => {
    return searchTasks.map((task: any) => {
      // Extract task metadata
      const location = task.kanbanColumn?.kanban?.title || task.project?.name || 'Unknown'
      const locationType = task.kanbanColumn?.title || (task.project ? 'Project' : '')
      const priorityClass = PRIORITY_COLORS[task.priority?.toLowerCase() as keyof typeof PRIORITY_COLORS] || 'text-gray-600'
      const statusIcon = STATUS_ICONS[task.status as keyof typeof STATUS_ICONS] || '○'
      
      return createCommand(
        `task-${task.id}`,
        task.title || 'Untitled Task',
        {
          subtitle: `${statusIcon} ${location}${locationType ? ` • ${locationType}` : ''} • ${task.assignee?.name || 'Unassigned'}`,
          icon: PlusIcon,
          action: () => {
            onOpenChange(false)
            setTimeout(() => {
              onAction?.(`open-task:${task.id}`)
              onNavigate?.(`/workspace/${workspaceId}#task=${task.id}`)
            }, 100)
          },
          group: CommandGroup.TASKS,
          keywords: [
            'task',
            task.title?.toLowerCase() || '',
            task.description?.toLowerCase() || '',
            task.assignee?.name?.toLowerCase() || '',
            location.toLowerCase(),
            task.priority?.toLowerCase() || ''
          ],
          priority: task.priority === 'critical' ? 100 :
                   task.priority === 'high' ? 90 :
                   task.priority === 'medium' ? 80 : 70,
          metadata: {
            priority: task.priority,
            status: task.status,
            priorityClass,
            location,
            locationType,
            assignee: task.assignee?.name
          }
        }
      )
    })
  }, [searchTasks, workspaceId, onOpenChange, onAction, onNavigate])

  // Note search commands factory
  const createNoteCommands = useCallback((): CommandItem[] => {
    return searchNotes.map((note: any) => {
      const contentPreview = note.content
        ? note.content.substring(0, 50).replace(/[\n\r]/g, ' ') + (note.content.length > 50 ? '...' : '')
        : 'No content'
      
      return createCommand(
        `note-${note.id}`,
        note.title || 'Untitled Note',
        {
          subtitle: `📝 ${contentPreview}`,
          icon: FileTextIcon,
          action: () => onNavigate?.(`/notes/${note.id}`),
          group: CommandGroup.NOTES,
          keywords: ['note', note.title?.toLowerCase() || '', note.content?.toLowerCase() || ''],
          priority: 50
        }
      )
    })
  }, [searchNotes, onNavigate])

  // Recent items commands factory
  const createRecentCommands = useCallback((): CommandItem[] => {
    return recentItems
      .filter((item: any) => item.title || item.name)
      .map((item: any) => {
        const title = item.title || item.name || 'Untitled'
        const date = item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 'Recently'
        const type = item.type || 'ITEM'
        
        return createCommand(
          `recent-${item.id}`,
          title,
          {
            subtitle: `${type.charAt(0) + type.slice(1).toLowerCase()} • ${date}`,
            icon: type === 'TASK' ? PlusIcon : type === 'NOTE' ? FileTextIcon : CalendarIcon,
            action: () => onNavigate?.(`/${type.toLowerCase()}/${item.id}`),
            group: CommandGroup.RECENT,
            keywords: ['recent', title.toLowerCase(), type.toLowerCase()],
            priority: 5
          }
        )
      })
  }, [recentItems, onNavigate])
  
  // Combine all commands using factory functions
  const allCommands = useMemo(() => [
    ...staticCommands,
    ...createWorkspaceCommands(),
    ...createTaskCommands(),
    ...createNoteCommands(),
    ...createRecentCommands()
  ], [
    staticCommands,
    createWorkspaceCommands,
    createTaskCommands,
    createNoteCommands,
    createRecentCommands
  ])
  
  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    return allCommands.filter(command => {
      // When there's a search query, only show matching items
      if (searchQuery) {
        // Always include backend-filtered results (Tasks, Notes)
        if (command.group === CommandGroup.TASKS || command.group === CommandGroup.NOTES) {
          return true // These are already filtered by the backend query
        }
        
        // Filter other commands by search query
        const query = searchQuery.toLowerCase()
        const titleMatch = command.title.toLowerCase().includes(query)
        const subtitleMatch = command.subtitle?.toLowerCase().includes(query)
        const keywordMatch = command.keywords?.some(keyword => keyword.includes(query))
        
        return titleMatch || subtitleMatch || keywordMatch
      }
      
      // No search query - show all commands
      return true
    })
  }, [allCommands, searchQuery])
  
  // Group filtered commands
  const groupedCommands = filteredCommands.reduce((acc, command) => {
    if (!acc[command.group]) acc[command.group] = []
    acc[command.group].push(command)
    return acc
  }, {} as Record<string, CommandItem[]>)
  
  // Debug logging
  useEffect(() => {
    if (searchQuery) {
      console.log('Search query:', searchQuery)
      console.log('Search tasks from backend:', searchTasks.length)
      console.log('Task commands created:', createTaskCommands().length)
      console.log('Filtered commands:', filteredCommands.length)
      console.log('Grouped commands:', Object.keys(groupedCommands))
    }
  }, [searchQuery, searchTasks, createTaskCommands, filteredCommands, groupedCommands])
  
  // Sort groups by priority using configuration
  const sortedGroups = useMemo(() => {
    const priorities = searchQuery ? GROUP_PRIORITIES.searching : GROUP_PRIORITIES.default
    return Object.entries(groupedCommands).sort(([a], [b]) => {
      const priorityA = priorities[a as CommandGroup] || 11
      const priorityB = priorities[b as CommandGroup] || 11
      return priorityA - priorityB
    })
  }, [groupedCommands, searchQuery])
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't process Cmd+K if user is typing
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (!shouldProcessHotkey(e)) {
          return
        }
        e.preventDefault()
        onOpenChange(true)
      }
      // Always allow Escape to close the command palette
      if (e.key === 'Escape') {
        onOpenChange(false)
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onOpenChange])
  
  return (
    <CommandDialog open={isOpen} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Search tasks, notes, commands... (type to filter)"
        value={searchQuery}
        onValueChange={setSearchQuery}
        className="text-base"
      />
      <CommandList>
        <CommandEmpty>
          <div className="text-center py-6">
            <CommandIcon className="w-8 h-8 mx-auto mb-2 text-krushr-secondary" />
            <p className="text-sidebar-foreground font-manrope">No commands found.</p>
            <p className="text-sm text-sidebar-foreground/60 mt-1 font-manrope">Try searching for actions, panels, or navigation</p>
          </div>
        </CommandEmpty>
        
        <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:px-2 lg:items-start">
          {sortedGroups.map(([group, commands], groupIndex) => (
            <CommandGroupComponent key={group} heading={group} className="lg:break-inside-avoid">
              {commands
                .sort((a, b) => (b.priority || 0) - (a.priority || 0))
                .map((command) => (
                  <CommandItem
                    key={command.id}
                    value={`${command.title} ${command.subtitle || ''} ${command.keywords?.join(' ') || ''}`}
                    onSelect={() => {
                      console.log('CommandItem onSelect triggered for:', command.id, command.title)
                      command.action()
                      // Don't close here for tasks - let the action handle it
                      if (command.group !== CommandGroup.TASKS) {
                        onOpenChange(false)
                      }
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl data-[selected=true]:bg-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <command.icon className={`w-4 h-4 ${(command as any).metadata?.priorityClass || 'text-krushr-secondary'} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-sidebar-foreground">
                        {command.title}
                        {group === CommandGroup.TASKS && searchQuery && (
                          <span className="ml-2 text-xs text-gray-500">
                            {(command as any).metadata?.status?.replace('_', ' ').toLowerCase()}
                          </span>
                        )}
                      </div>
                      {command.subtitle && (
                        <div className="text-xs text-sidebar-foreground/70 truncate">{command.subtitle}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {command.shortcut && (
                        <CommandShortcut className="text-xs">{command.shortcut}</CommandShortcut>
                      )}
                      {group === CommandGroup.AI_ASSISTANT && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0.5 font-manrope">
                          AI
                        </Badge>
                      )}
                    </div>
                  </CommandItem>
                ))}
            </CommandGroupComponent>
          ))}
        </div>
      </CommandList>
    </CommandDialog>
  )
}